import { z } from 'zod';
import { diasEntre } from './fiscal';
import { parcelaPriceCentavos } from './juros';

// ─────────── Pagamentos da venda: Pix, dinheiro, cartão, boleto, cheque, crediário e financiamento ───────────

export const MEIOS_PAGAMENTO = [
  'dinheiro',
  'pix',
  'transferencia',
  'debito',
  'credito',
  'boleto',
  'cheque',
  'crediario',
  'financiamento',
] as const;
export type MeioPagamento = (typeof MEIOS_PAGAMENTO)[number];

export const NOME_MEIO: Record<MeioPagamento, string> = {
  dinheiro: 'Dinheiro',
  pix: 'Pix',
  transferencia: 'Transferência (TED)',
  debito: 'Cartão de débito',
  credito: 'Cartão de crédito',
  boleto: 'Boleto',
  cheque: 'Cheque',
  crediario: 'Crediário da loja',
  financiamento: 'Financiamento (banco)',
};

/** Recebidos na hora (caem direto no caixa ou no banco no dia da venda). */
export const MEIOS_NA_HORA: readonly MeioPagamento[] = ['dinheiro', 'pix', 'transferencia'];
/** Podem ser parcelados. */
export const MEIOS_PARCELADOS: readonly MeioPagamento[] = [
  'credito',
  'boleto',
  'cheque',
  'crediario',
];
/** Precisam da data do primeiro vencimento. */
export const MEIOS_COM_VENCIMENTO: readonly MeioPagamento[] = ['boleto', 'cheque', 'crediario'];

export const MAX_PARCELAS: Record<MeioPagamento, number> = {
  dinheiro: 1,
  pix: 1,
  transferencia: 1,
  debito: 1,
  credito: 21,
  boleto: 24,
  cheque: 24,
  crediario: 60,
  financiamento: 1,
};

/** Crediário: multa por atraso (2%, teto do CDC) e juros de mora ao mês (1%), em centésimos de ponto. */
export const MULTA_ATRASO_PADRAO = 200;
export const MORA_MENSAL_PADRAO = 100;

const centavos = z.number().int().min(0).max(100_000_000_00);
const positivo = centavos.refine((v) => v > 0, 'Valor deve ser maior que zero');
/** Percentual em centésimos de ponto (199 = 1,99%). */
const pct = (max: number) => z.number().int().min(0).max(max);
const dia = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida (AAAA-MM-DD)')
  .refine((v) => !Number.isNaN(new Date(`${v}T00:00:00Z`).getTime()), 'Data inválida');
const textoOpc = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export const pagamentoVendaSchema = z
  .object({
    meio: z.enum(MEIOS_PAGAMENTO),
    valorCentavos: positivo,
    parcelas: z.number().int().min(1).max(60).default(1),
    /** débito e crédito: maquininha usada (define taxa e prazo) */
    maquininhaId: z.string().uuid().nullable().optional(),
    /** crédito: receber tudo de uma vez (antecipação, com a taxa de antecipação) */
    antecipar: z.boolean().default(false),
    /** boleto, cheque e crediário: primeiro vencimento; os outros vencem todo mês */
    primeiroVencimento: dia.nullable().optional(),
    /** crediário: juros ao mês em centésimos de ponto (199 = 1,99% a.m.) */
    jurosMensal: pct(1_000).default(0),
    /** financiamento: banco */
    banco: textoOpc(80),
  })
  .superRefine((p, c) => {
    if (p.parcelas > MAX_PARCELAS[p.meio])
      c.addIssue({
        code: 'custom',
        path: ['parcelas'],
        message:
          MAX_PARCELAS[p.meio] === 1
            ? `${NOME_MEIO[p.meio]} não é parcelado`
            : `${NOME_MEIO[p.meio]}: no máximo ${MAX_PARCELAS[p.meio]} parcelas`,
      });
    if (MEIOS_COM_VENCIMENTO.includes(p.meio) && !p.primeiroVencimento)
      c.addIssue({
        code: 'custom',
        path: ['primeiroVencimento'],
        message: 'Informe o primeiro vencimento',
      });
    if (p.jurosMensal > 0 && p.meio !== 'crediario')
      c.addIssue({ code: 'custom', path: ['jurosMensal'], message: 'Juros só no crediário' });
    if (p.antecipar && p.meio !== 'credito')
      c.addIssue({ code: 'custom', path: ['antecipar'], message: 'Antecipação só no crédito' });
  });
export type PagamentoVenda = z.infer<typeof pagamentoVendaSchema>;
export type PagamentoVendaEntrada = z.input<typeof pagamentoVendaSchema>;

export const pagamentosVendaSchema = z
  .array(pagamentoVendaSchema)
  .max(10, 'No máximo 10 formas de pagamento')
  .refine(
    (l) => l.filter((p) => p.meio === 'financiamento').length <= 1,
    'Só um financiamento por venda',
  );

// ─────────── maquininhas (taxas cadastradas pela loja) ───────────

export const maquininhaSchema = z.object({
  nome: z.string().trim().min(2, 'Dê um nome (ex.: Stone do balcão)').max(60),
  /** centésimos de ponto: 109 = 1,09% */
  taxaDebito: pct(2_000),
  /** taxa do crédito por número de parcelas: posição 0 = à vista (1x), 1 = 2x… */
  taxasCredito: z.array(pct(3_000)).min(1).max(21),
  /** antecipação: % ao mês sobre o prazo médio das parcelas */
  taxaAntecipacaoMes: pct(1_000).default(0),
  prazoDebitoDias: z.number().int().min(0).max(60).default(1),
  prazoCreditoDias: z.number().int().min(1).max(60).default(30),
  ativa: z.boolean().default(true),
});
export type MaquininhaEntrada = z.input<typeof maquininhaSchema>;
export interface Maquininha {
  id: string;
  nome: string;
  taxaDebito: number;
  taxasCredito: number[];
  taxaAntecipacaoMes: number;
  prazoDebitoDias: number;
  prazoCreditoDias: number;
  ativa: boolean;
}

/** Taxa do crédito em n parcelas; se a loja informou menos faixas, vale a última. */
export const taxaCredito = (m: Pick<Maquininha, 'taxasCredito'>, n: number) =>
  m.taxasCredito[Math.min(n, m.taxasCredito.length) - 1] ?? 0;

/** Taxa total (centésimos de ponto) do crédito, com antecipação pelo prazo médio das parcelas. */
export const taxaCreditoTotal = (
  m: Pick<Maquininha, 'taxasCredito' | 'taxaAntecipacaoMes'>,
  n: number,
  antecipar: boolean,
) => taxaCredito(m, n) + (antecipar ? Math.round((m.taxaAntecipacaoMes * (n + 1)) / 2) : 0);

// ─────────── datas e divisão ───────────

const diaUtc = (d: string) => new Date(`${d}T00:00:00.000Z`);
export const somarDiasData = (d: string, n: number) =>
  new Date(diaUtc(d).getTime() + n * 86_400_000).toISOString().slice(0, 10);
/** Soma meses mantendo o dia (31/01 + 1 mês = 28 ou 29/02). */
export function somarMesesData(d: string, n: number): string {
  const [a, m, di] = d.split('-').map(Number) as [number, number, number];
  const alvo = new Date(Date.UTC(a, m - 1 + n, 1));
  const ultimo = new Date(Date.UTC(alvo.getUTCFullYear(), alvo.getUTCMonth() + 1, 0)).getUTCDate();
  alvo.setUTCDate(Math.min(di, ultimo));
  return alvo.toISOString().slice(0, 10);
}

/** Divide em n partes inteiras; os centavos que sobram vão na primeira. */
export function dividir(total: number, n: number): number[] {
  const base = Math.floor(total / n);
  return Array.from({ length: n }, (_, i) => (i === 0 ? total - base * (n - 1) : base));
}

/** Tabela Price: parcelas iguais; a última acerta os centavos. Juros em centésimos de ponto ao mês. */
export function tabelaPrice(
  principal: number,
  jurosMensal: number,
  n: number,
): { valorCentavos: number; jurosCentavos: number; amortizacaoCentavos: number }[] {
  const i = jurosMensal / 10_000;
  if (i === 0)
    return dividir(principal, n).map((v) => ({
      valorCentavos: v,
      jurosCentavos: 0,
      amortizacaoCentavos: v,
    }));
  const pmt = parcelaPriceCentavos(principal, i, n);
  let saldo = principal;
  const linhas = [];
  for (let k = 1; k <= n; k++) {
    const juros = Math.round(saldo * i);
    const amort = k === n ? saldo : pmt - juros;
    saldo -= amort;
    linhas.push({ valorCentavos: amort + juros, jurosCentavos: juros, amortizacaoCentavos: amort });
  }
  return linhas;
}

// ─────────── plano de recebimentos ───────────

export interface Recebimento {
  /** posição do pagamento na lista da venda */
  pagamento: number;
  meio: MeioPagamento;
  descricao: string;
  /** valor bruto que o cliente pagou (parcela) */
  valorCentavos: number;
  /** taxa da maquininha (despesa da loja) */
  taxaCentavos: number;
  /** juros do crediário dentro da parcela (receita da loja) */
  jurosCentavos: number;
  vencimento: string;
  /** já entrou no caixa/banco no dia da venda */
  recebido: boolean;
  parcela: number | null;
  parcelas: number | null;
  /** de quem a loja recebe */
  de: 'cliente' | 'maquininha';
  maquininhaId: string | null;
}

const MAQUININHA_SEM_TAXA: Maquininha = {
  id: '',
  nome: 'Maquininha',
  taxaDebito: 0,
  taxasCredito: [0],
  taxaAntecipacaoMes: 0,
  prazoDebitoDias: 1,
  prazoCreditoDias: 30,
  ativa: true,
};

/**
 * O que a loja vai receber, quando e de quem, para cada pagamento da venda. O financiamento do
 * banco fica de fora (tem o próprio lançamento, com o retorno do banco).
 */
export function planejarRecebimentos(
  data: string,
  pagamentos: PagamentoVenda[],
  maquininhas: Maquininha[],
): Recebimento[] {
  const r: Recebimento[] = [];
  pagamentos.forEach((p, idx) => {
    const base = {
      pagamento: idx,
      meio: p.meio,
      taxaCentavos: 0,
      jurosCentavos: 0,
      parcela: null,
      parcelas: null,
      de: 'cliente' as const,
      maquininhaId: null,
    };
    const nome = NOME_MEIO[p.meio];
    switch (p.meio) {
      case 'dinheiro':
      case 'pix':
      case 'transferencia':
        r.push({
          ...base,
          descricao: nome,
          valorCentavos: p.valorCentavos,
          vencimento: data,
          recebido: true,
        });
        break;
      case 'debito': {
        const m = maquininhas.find((x) => x.id === p.maquininhaId) ?? MAQUININHA_SEM_TAXA;
        r.push({
          ...base,
          descricao: `${nome}${m.id ? ` · ${m.nome}` : ''}`,
          valorCentavos: p.valorCentavos,
          taxaCentavos: Math.round((p.valorCentavos * m.taxaDebito) / 10_000),
          vencimento: somarDiasData(data, m.prazoDebitoDias),
          recebido: false,
          de: 'maquininha',
          maquininhaId: m.id || null,
        });
        break;
      }
      case 'credito': {
        const m = maquininhas.find((x) => x.id === p.maquininhaId) ?? MAQUININHA_SEM_TAXA;
        const n = p.parcelas;
        const taxa = taxaCreditoTotal(m, n, p.antecipar);
        const rotulo = `${nome} ${n}x${m.id ? ` · ${m.nome}` : ''}`;
        if (p.antecipar || n === 1) {
          r.push({
            ...base,
            descricao: p.antecipar ? `${rotulo} (antecipado)` : rotulo,
            valorCentavos: p.valorCentavos,
            taxaCentavos: Math.round((p.valorCentavos * taxa) / 10_000),
            vencimento: somarDiasData(data, p.antecipar ? 1 : m.prazoCreditoDias),
            recebido: false,
            parcelas: n,
            de: 'maquininha',
            maquininhaId: m.id || null,
          });
          break;
        }
        dividir(p.valorCentavos, n).forEach((v, i) =>
          r.push({
            ...base,
            descricao: `${rotulo} · parcela ${i + 1}/${n}`,
            valorCentavos: v,
            taxaCentavos: Math.round((v * taxa) / 10_000),
            vencimento: somarDiasData(data, m.prazoCreditoDias * (i + 1)),
            recebido: false,
            parcela: i + 1,
            parcelas: n,
            de: 'maquininha',
            maquininhaId: m.id || null,
          }),
        );
        break;
      }
      case 'boleto':
      case 'cheque':
      case 'crediario': {
        const n = p.parcelas;
        const linhas =
          p.meio === 'crediario'
            ? tabelaPrice(p.valorCentavos, p.jurosMensal, n)
            : dividir(p.valorCentavos, n).map((v) => ({ valorCentavos: v, jurosCentavos: 0 }));
        linhas.forEach((l, i) =>
          r.push({
            ...base,
            descricao: n > 1 ? `${nome} · parcela ${i + 1}/${n}` : nome,
            valorCentavos: l.valorCentavos,
            jurosCentavos: l.jurosCentavos,
            vencimento: somarMesesData(p.primeiroVencimento ?? data, i),
            recebido: false,
            parcela: n > 1 ? i + 1 : null,
            parcelas: n > 1 ? n : null,
          }),
        );
        break;
      }
      case 'financiamento':
        break;
    }
  });
  return r;
}

/** Resumo para a tela: total pago pelo cliente, taxas e juros, e quanto a loja recebe líquido. */
export function resumoRecebimentos(r: Recebimento[]) {
  const bruto = r.reduce((s, x) => s + x.valorCentavos, 0);
  const taxas = r.reduce((s, x) => s + x.taxaCentavos, 0);
  const juros = r.reduce((s, x) => s + x.jurosCentavos, 0);
  const naHora = r.filter((x) => x.recebido).reduce((s, x) => s + x.valorCentavos, 0);
  return {
    brutoCentavos: bruto,
    taxasCentavos: taxas,
    jurosCentavos: juros,
    liquidoCentavos: bruto - taxas,
    naHoraCentavos: naHora,
    aReceberCentavos: bruto - naHora,
  };
}

/** Parcela em atraso: multa uma vez + juros de mora proporcionais aos dias. */
export function valorAtualizado(
  valorCentavos: number,
  vencimento: string,
  hoje: string,
  multa = MULTA_ATRASO_PADRAO,
  moraMensal = MORA_MENSAL_PADRAO,
): { valorCentavos: number; multaCentavos: number; moraCentavos: number; diasAtraso: number } {
  const dias = diasEntre(vencimento, hoje);
  if (dias <= 0) return { valorCentavos, multaCentavos: 0, moraCentavos: 0, diasAtraso: 0 };
  const m = Math.round((valorCentavos * multa) / 10_000);
  const j = Math.round((valorCentavos * moraMensal * dias) / 10_000 / 30);
  return {
    valorCentavos: valorCentavos + m + j,
    multaCentavos: m,
    moraCentavos: j,
    diasAtraso: dias,
  };
}

// ─────────── valor por extenso (recibo) ───────────

const UNIDADES = [
  '',
  'um',
  'dois',
  'três',
  'quatro',
  'cinco',
  'seis',
  'sete',
  'oito',
  'nove',
  'dez',
  'onze',
  'doze',
  'treze',
  'catorze',
  'quinze',
  'dezesseis',
  'dezessete',
  'dezoito',
  'dezenove',
];
const DEZENAS = [
  '',
  '',
  'vinte',
  'trinta',
  'quarenta',
  'cinquenta',
  'sessenta',
  'setenta',
  'oitenta',
  'noventa',
];
const CENTENAS = [
  '',
  'cento',
  'duzentos',
  'trezentos',
  'quatrocentos',
  'quinhentos',
  'seiscentos',
  'setecentos',
  'oitocentos',
  'novecentos',
];

function ate999(n: number): string {
  if (n === 100) return 'cem';
  const partes: string[] = [];
  const c = Math.floor(n / 100);
  const r = n % 100;
  if (c) partes.push(CENTENAS[c]!);
  if (r) {
    if (r < 20) partes.push(UNIDADES[r]!);
    else {
      const d = Math.floor(r / 10);
      const u = r % 10;
      partes.push(u ? `${DEZENAS[d]} e ${UNIDADES[u]}` : DEZENAS[d]!);
    }
  }
  return partes.join(' e ');
}

function inteiroPorExtenso(n: number): string {
  if (n === 0) return 'zero';
  const escalas: [number, string, string][] = [
    [1_000_000_000, 'bilhão', 'bilhões'],
    [1_000_000, 'milhão', 'milhões'],
    [1_000, 'mil', 'mil'],
  ];
  const grupos: { valor: number; texto: string }[] = [];
  let resto = n;
  for (const [div, sing, plur] of escalas) {
    const q = Math.floor(resto / div);
    resto %= div;
    if (!q) continue;
    grupos.push({
      valor: q * div,
      texto: div === 1_000 && q === 1 ? 'mil' : `${ate999(q)} ${q === 1 ? sing : plur}`,
    });
  }
  if (resto) grupos.push({ valor: resto, texto: ate999(resto) });
  return grupos
    .map((g, i) => {
      if (i === 0) return g.texto;
      const ultimo = i === grupos.length - 1;
      const e = ultimo && (g.valor < 100 || g.valor % 100 === 0);
      return `${e ? ' e ' : ' '}${g.texto}`;
    })
    .join('');
}

/** "R$ 1.250,50" → "mil duzentos e cinquenta reais e cinquenta centavos". */
export function valorPorExtenso(centavosTotal: number): string {
  const reais = Math.floor(centavosTotal / 100);
  const cent = centavosTotal % 100;
  const partes: string[] = [];
  if (reais) {
    const de = reais >= 1_000_000 && reais % 1_000_000 === 0 ? ' de' : '';
    partes.push(`${inteiroPorExtenso(reais)}${de} ${reais === 1 ? 'real' : 'reais'}`);
  }
  if (cent) partes.push(`${inteiroPorExtenso(cent)} ${cent === 1 ? 'centavo' : 'centavos'}`);
  return partes.length ? partes.join(' e ') : 'zero reais';
}

// ─────────── caixa do dia (dinheiro) ───────────

export const caixaFecharSchema = z.object({
  filialId: z.string().uuid(),
  data: dia,
  /** quanto tem na gaveta contado no fim do dia */
  contadoCentavos: centavos,
  observacao: textoOpc(300),
});
export type CaixaFechar = z.infer<typeof caixaFecharSchema>;

// ─────────── cobrança automática do crediário ───────────

export const cobrancaCrediarioSchema = z.object({
  /** manda o Pix da parcela antes do vencimento e avisa os atrasos (modo piloto: começa desligada) */
  ativo: z.boolean().default(false),
  /** quantos dias antes do vencimento o Pix é enviado (0 = no dia) */
  diasAntes: z.number().int().min(0).max(15).default(5),
  avisarAtraso: z.boolean().default(true),
  /** de quantos em quantos dias o aviso de atraso se repete (até 90 dias) */
  repetirAtrasoDias: z.number().int().min(1).max(30).default(7),
  /** avisa dono, gerente, financeiro e o vendedor do cliente quando atrasa */
  avisarLoja: z.boolean().default(true),
});
export type CobrancaCrediarioConfig = z.infer<typeof cobrancaCrediarioSchema>;
export const COBRANCA_CREDIARIO_PADRAO: CobrancaCrediarioConfig = cobrancaCrediarioSchema.parse({});

/** Hoje é dia de avisar este atraso? No 1º dia e depois a cada N dias, até 90. */
export function diaDeAvisarAtraso(diasAtraso: number, repetir: number): boolean {
  if (diasAtraso < 1 || diasAtraso > 90) return false;
  return diasAtraso === 1 || (diasAtraso - 1) % repetir === 0;
}

const reaisTexto = (c: number) =>
  (c / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataCurtaTexto = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

/** Texto da mensagem (WhatsApp ou e-mail) e variáveis do modelo aprovado na Meta, na ordem. */
export function mensagemCobranca(
  tipo: 'lembrete' | 'atraso',
  d: {
    nome: string;
    parcela: string;
    valorCentavos: number;
    vencimento: string;
    diasAtraso?: number;
    pix: string | null;
    loja: string;
  },
): { texto: string; parametros: string[]; modelo: string } {
  const primeiro = d.nome.split(' ')[0] ?? d.nome;
  const valor = reaisTexto(d.valorCentavos);
  const pix = d.pix ?? 'peça o Pix à loja';
  if (tipo === 'lembrete')
    return {
      modelo: 'cobranca_parcela',
      parametros: [primeiro, d.parcela, valor, dataCurtaTexto(d.vencimento), pix, d.loja],
      texto: `Olá, ${primeiro}! A parcela ${d.parcela} do seu carro, de ${valor}, vence em ${dataCurtaTexto(d.vencimento)}. Pix copia e cola: ${pix}. ${d.loja}`,
    };
  return {
    modelo: 'parcela_atrasada',
    parametros: [primeiro, d.parcela, String(d.diasAtraso ?? 1), valor, pix, d.loja],
    texto: `Olá, ${primeiro}. A parcela ${d.parcela} do seu carro está em aberto há ${d.diasAtraso ?? 1} dia(s). Valor atualizado: ${valor}. Pix copia e cola: ${pix}. Se já pagou, desconsidere. ${d.loja}`,
  };
}
